// FIX-CCS-G-GPS unit G4 — FIX-GPS-ENGINE: the location engine as a pure state machine (GPS-3 S2 §2.3, S7 §7.3).
//
// reduce(state, event) → { state, effects, emits }. No browser, no clock, no randomness: every time comes in on the
// event. The adapter (locationEngine.ts) turns effects into navigator.geolocation calls and emits into bundles.
//
// G-A unit 4 — ruling R2 (8 Oct): the photo's location is decided AT THE PICK, from fixes taken before the pick:
//   · a fix at most N_s (freshness_s, 15 s) old at the pick → sent, and the server may store it 'verified';
//   · else the newest fix at most STALE_MAX_s (stale_max_s, read from get_gps_guarantee_policy — never hard-coded,
//     SD-O112) old → sent as the location, NOT verified (the server stores it 'unverified');
//   · else → no fix (the server may still use the photo's own EXIF GPS, ruling R4 — that is the server's job).
// A fix taken after the pick is never attached: a queued photo carries the pick-time fix only, never one from where
// it is later sent. Every fix must be accurate (acc_m ≤ A_hard).
// SD-O65 (client half): while the phone's camera app is open (ARM → pick) the watch keeps running and nothing heard is
// dropped, so the fix closest to the pick is still there when the page comes back.
// Freshness is judged on position.timestamp, never on arrival time: a cached replay (the old maximumAge: 60000 bug,
// SD-GG-8) arrives now but carries an old timestamp and is ignored.

export type Perm = 'granted' | 'prompt' | 'denied' | 'unknown';
export type Status =
  | 'stopped' | 'paused' | 'needs_permission' | 'no_permission' | 'location_off'
  | 'warming' | 'ready' | 'weak' | 'approximate' | 'unavailable';
export type Source = 'browser_watch';

export interface Fix {
  lat: number; lng: number; acc_m: number;
  fix_ts: number; // position.timestamp (ms)
  recv_ts: number; // Date.now() at the callback (ms)
  alt?: number | null; alt_acc?: number | null; speed?: number | null; heading?: number | null;
  skew_clamped?: boolean;
}

export interface Params {
  N_s: number; A_hard: number; A_good: number; IDLE_s: number;
  /** R2: the oldest fix (s at the pick) still sent as the location, unverified. null = the policy is not read yet →
   *  only a fresh fix is attached (a fallback never widens what is sent). */
  STALE_MAX_s: number | null;
  /** How long after the tap the phone's camera app counts as open (SD-O65: keep the watch alive while hidden). */
  CAMERA_MAX_s: number;
  APPROX_acc: number; APPROX_window_s: number; APPROX_min_fixes: number; APPROX_spread: number; APPROX_drift_m: number;
  BUFFER_s: number; SKEW_ms: number; rule_version: string;
}
export const DEFAULT_PARAMS: Params = {
  N_s: 15, A_hard: 100, A_good: 30, IDLE_s: 180, STALE_MAX_s: null, CAMERA_MAX_s: 300,
  APPROX_acc: 1000, APPROX_window_s: 30, APPROX_min_fixes: 3, APPROX_spread: 0.10, APPROX_drift_m: 5,
  BUFFER_s: 120, SKEW_ms: 2000, rule_version: 'le-2.0:R2/N15/A100/G30',
};
/** U1: exactly these options — never a cached position (maximumAge 0). */
export const WATCH_OPTIONS = { enableHighAccuracy: true, maximumAge: 0 } as const;

/** The farmer tapped the shutter button; the phone's own camera app is about to open (the page usually hides).
 *  FIX-CCS-POLISH-4 B4: `fixes` = the accurate fixes that were fresh AT THE TAP (the tap-fix rule at the pick). */
export interface Armed { photo_id: string; ts: number; hid: boolean; fixes?: Fix[] }

export interface Attempt {
  watch_started_at: number | null; first_fix_ms: number | null; fix_count: number; best_accuracy_m: number | null;
  accuracy_series: number[]; errors: Record<string, number>; visible_ms: number; resumed_count: number;
  stale_ignored: number; skew_clamped: number; rejected_fixes: number; perm_changes: number; prompt_shown: boolean;
  ttff_after_resume_ms: number[];
}
export interface State {
  params: Params; status: Status; perm: Perm; perm_ts: number | null; watching: boolean; hidden: boolean;
  fixes: Fix[]; demands: string[]; last_demand_ts: number | null; last_release_ts: number | null;
  /** The newest accurate fix heard (kept across a hide and past BUFFER_s; its age at the pick decides R2). */
  last_good: Fix | null;
  visible_ms_accum: number; last_visible_ts: number; resume_ts: number | null; approximate_inferred: boolean;
  requested: boolean; attempt: Attempt; armed: Armed[];
}

export type Event =
  | { type: 'DEMAND'; task_id: string; ts: number }
  | { type: 'RELEASE'; task_id: string; ts: number }
  | { type: 'REQUEST'; ts: number } // the farmer tapped "use my location" (user intent; the browser may prompt)
  | { type: 'PERM'; state: Perm; ts: number }
  | { type: 'FIX'; fix: Fix }
  | { type: 'ERROR'; code: number; ts: number }
  | { type: 'HIDDEN'; ts: number }
  | { type: 'VISIBLE'; ts: number }
  | { type: 'TICK'; ts: number }
  | { type: 'ARM'; photo_id: string; ts: number }
  /** capture_ts: when the photo was TAKEN, best evidence first (EXIF, then file time) — B4 tap-fix rule only. */
  | { type: 'SHUTTER'; photo_id: string; ts: number; capture_ts?: number[] }
  | { type: 'POLICY'; freshness_s: number | null; stale_max_s: number | null }
  | { type: 'UNAVAILABLE'; ts: number };

export type Effect = { type: 'START_WATCH'; options: typeof WATCH_OPTIONS } | { type: 'CLEAR_WATCH' };

export interface BundleCore {
  schema: 'agxl.location_bundle.v1'; rule_version: string; source: Source;
  lat: number; lng: number; accuracy_m: number;
  altitude_m: number | null; altitude_accuracy_m: number | null; speed_mps: number | null; heading_deg: number | null;
  fix_time: string; shutter_time: string; fix_age_s: number;
  post_shutter_delay_s: null; hold_visible_s: null; movement_m: null; movement_basis: 'unknown';
  quality: 'good' | 'acceptable'; engine_status_at_shutter: Status; approximate_inferred: boolean;
  /** B4: present when the fix is the one held at the shutter tap (shutter_time = the photo's capture moment). */
  pick_rule?: 'tap_fix';
  permission: { state: Perm; queried_at: string | null; prompt_shown: boolean };
  attempt: {
    watch_started_at: string | null; first_fix_ms: number | null; fix_count: number; best_accuracy_m: number | null;
    accuracy_series: number[]; errors: Record<string, number>; visible_s: number; resumed_count: number;
  };
  exif: null;
}
/** Why a pick has no fix: nothing accurate heard before it, or the newest one is too old (or the limit is unknown). */
export type NoFixReason = 'no_fix' | 'too_old';
export type Emit =
  | { type: 'BUNDLE'; photo_id: string; bundle: BundleCore; verified: boolean }
  | { type: 'NO_FIX'; photo_id: string; reason: NoFixReason; status: Status };

export interface Result { state: State; effects: Effect[]; emits: Emit[] }

// ── helpers (pure, exported for tests) ──

const iso = (ms: number) => new Date(ms).toISOString();
const isoOrNull = (ms: number | null) => (ms === null ? null : iso(ms));

export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371000, r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function initialState(params: Params = DEFAULT_PARAMS, perm: Perm = 'unknown', ts = 0): State {
  return {
    params, status: 'stopped', perm, perm_ts: null, watching: false, hidden: false, fixes: [], demands: [],
    last_demand_ts: null, last_release_ts: null, last_good: null, visible_ms_accum: 0, last_visible_ts: ts, resume_ts: null,
    approximate_inferred: false, requested: false, armed: [],
    attempt: {
      watch_started_at: null, first_fix_ms: null, fix_count: 0, best_accuracy_m: null, accuracy_series: [], errors: {},
      visible_ms: 0, resumed_count: 0, stale_ignored: 0, skew_clamped: 0, rejected_fixes: 0, perm_changes: 0,
      prompt_shown: false, ttff_after_resume_ms: [],
    },
  };
}

export function nowVisible(s: State, ts: number): number {
  return s.visible_ms_accum + (s.hidden ? 0 : Math.max(0, ts - s.last_visible_ts));
}

/** Fixes whose position.timestamp is within N_s of ts (and not after it). */
export function freshFixes(s: State, ts: number): Fix[] {
  return s.fixes.filter((f) => f.fix_ts <= ts && ts - f.fix_ts <= s.params.N_s * 1000);
}

/** Most accurate fresh fix; on a tie the newer one (U17). */
export function bestFix(s: State, ts: number): Fix | null {
  let b: Fix | null = null;
  for (const f of freshFixes(s, ts)) {
    if (!b || f.acc_m < b.acc_m || (f.acc_m === b.acc_m && f.fix_ts > b.fix_ts)) b = f;
  }
  return b;
}

export type PickVerdict =
  | { kind: 'verified'; fix: Fix }
  | { kind: 'unverified'; fix: Fix }
  | { kind: 'none'; reason: NoFixReason };

/**
 * R2 — what a pick at `ts` would carry. Only fixes taken at or before `ts` and accurate (≤ A_hard) count.
 * The chip shows this live; the SHUTTER applies it once.
 */
export function pickVerdict(s: State, ts: number): PickVerdict {
  const p = s.params;
  const ok = (f: Fix | null): f is Fix => !!f && f.fix_ts <= ts && f.acc_m <= p.A_hard;
  const fresh = bestFix({ ...s, fixes: s.fixes.filter(ok) }, ts);
  if (fresh) return { kind: 'verified', fix: fresh };
  let newest: Fix | null = null;
  for (const f of [...s.fixes, s.last_good]) if (ok(f) && (!newest || f.fix_ts > newest.fix_ts)) newest = f;
  if (!newest) return { kind: 'none', reason: 'no_fix' };
  if (p.STALE_MAX_s !== null && ts - newest.fix_ts <= p.STALE_MAX_s * 1000) return { kind: 'unverified', fix: newest };
  return { kind: 'none', reason: 'too_old' };
}

/** B4: the tap-fix verdict for a pick, or null when the rule does not apply (exported for tests). */
export function tapFixVerdict(
  armed: Armed | null,
  ev: { ts: number; capture_ts?: number[] },
  p: Params,
): { fix: Fix; capture_ts: number } | null {
  if (!armed || !armed.fixes?.length || !ev.capture_ts?.length) return null;
  // the photo's capture moment: the first candidate between the tap and the pick (clock skew tolerated)
  const cap = ev.capture_ts.find((c) => Number.isFinite(c) && c >= armed.ts - p.SKEW_ms && c <= ev.ts + p.SKEW_ms);
  if (cap === undefined) return null;
  // the most accurate fix held at the tap that is ≤ A_hard and at most N_s before the capture (newer wins a tie)
  let best: Fix | null = null;
  for (const f of armed.fixes) {
    if (!(f.acc_m <= p.A_hard) || f.fix_ts > cap || cap - f.fix_ts > p.N_s * 1000) continue;
    if (!best || f.acc_m < best.acc_m || (f.acc_m === best.acc_m && f.fix_ts > best.fix_ts)) best = f;
  }
  return best ? { fix: best, capture_ts: cap } : null;
}

/** D3 CONC 3: "approximate" only on a stable signature of ≥ 3 coarse fixes — never on one fix. */
export function approximateSignature(s: State, ts: number): boolean {
  const p = s.params;
  const w = s.fixes.filter((f) => ts - f.fix_ts <= p.APPROX_window_s * 1000);
  if (w.length < p.APPROX_min_fixes) return false;
  if (!w.every((f) => f.acc_m > p.APPROX_acc)) return false;
  const accs = w.map((f) => f.acc_m).sort((a, b) => a - b);
  const median = accs[Math.floor(accs.length / 2)];
  if ((accs[accs.length - 1] - accs[0]) / median >= p.APPROX_spread) return false;
  for (let i = 0; i < w.length; i++) for (let j = i + 1; j < w.length; j++) if (distanceM(w[i], w[j]) >= p.APPROX_drift_m) return false;
  return true;
}

const SLEEPING: Status[] = ['paused', 'stopped', 'no_permission', 'needs_permission', 'unavailable'];

function classify(s: State, ts: number): State {
  if (SLEEPING.includes(s.status)) return s;
  const b = bestFix(s, ts);
  if (b && b.acc_m <= s.params.A_hard) return s.status === 'ready' ? s : { ...s, status: 'ready' };
  if (approximateSignature(s, ts)) return { ...s, status: 'approximate', approximate_inferred: true };
  if (!b) return s.status === 'location_off' ? s : { ...s, status: 'warming' };
  return { ...s, status: 'weak' };
}

/** SD-O65: the phone's camera app is open (a tap within CAMERA_MAX_s that has not been picked yet). */
export function cameraOpen(s: State, ts: number): boolean {
  return s.armed.some((a) => ts - a.ts <= s.params.CAMERA_MAX_s * 1000);
}

function wantsWatch(s: State, ts: number): boolean {
  return s.demands.length > 0 && (!s.hidden || cameraOpen(s, ts)) && s.status !== 'unavailable'
    && s.perm !== 'denied' && (s.perm !== 'prompt' || s.requested);
}

function startIfWanted(s: State, ts: number, effects: Effect[]): State {
  if (s.watching || !wantsWatch(s, ts)) return s;
  effects.push({ type: 'START_WATCH', options: WATCH_OPTIONS });
  const attempt = s.attempt.watch_started_at === null ? { ...s.attempt, watch_started_at: ts } : s.attempt;
  return { ...s, watching: true, status: 'warming', attempt };
}

function stopWatch(s: State, effects: Effect[]): State {
  if (!s.watching) return s;
  effects.push({ type: 'CLEAR_WATCH' });
  return { ...s, watching: false };
}

/** The status when no watch is running. */
function idleStatus(s: State): Status {
  if (s.perm === 'denied') return 'no_permission';
  if (s.demands.length === 0) return 'stopped';
  return 'needs_permission'; // demand exists but no watch → only an un-asked 'prompt' gets here
}

function prune(s: State, ts: number): State {
  const keep = s.fixes.filter((f) => ts - f.fix_ts <= s.params.BUFFER_s * 1000);
  return keep.length === s.fixes.length ? s : { ...s, fixes: keep };
}

function makeBundle(s: State, f: Fix, shutter_ts: number, statusAtShutter: Status): BundleCore {
  const a = s.attempt;
  return {
    schema: 'agxl.location_bundle.v1', rule_version: s.params.rule_version, source: 'browser_watch',
    lat: f.lat, lng: f.lng, accuracy_m: f.acc_m,
    altitude_m: f.alt ?? null, altitude_accuracy_m: f.alt_acc ?? null, speed_mps: f.speed ?? null, heading_deg: f.heading ?? null,
    fix_time: iso(f.fix_ts), shutter_time: iso(shutter_ts), fix_age_s: (shutter_ts - f.fix_ts) / 1000,
    post_shutter_delay_s: null, hold_visible_s: null, movement_m: null, movement_basis: 'unknown',
    quality: f.acc_m <= s.params.A_good ? 'good' : 'acceptable',
    engine_status_at_shutter: statusAtShutter, approximate_inferred: s.approximate_inferred,
    permission: { state: s.perm, queried_at: isoOrNull(s.perm_ts), prompt_shown: a.prompt_shown },
    attempt: {
      watch_started_at: isoOrNull(a.watch_started_at), first_fix_ms: a.first_fix_ms,
      fix_count: a.fix_count, best_accuracy_m: a.best_accuracy_m, accuracy_series: a.accuracy_series.slice(-60),
      errors: { ...a.errors }, visible_s: Math.round(a.visible_ms / 1000), resumed_count: a.resumed_count,
    },
    exif: null,
  };
}

function idleCheck(s: State, ts: number, effects: Effect[]): State {
  if (s.demands.length > 0 || s.status === 'stopped' || s.status === 'unavailable') return s;
  const since = s.last_release_ts ?? s.last_demand_ts;
  if (since === null || ts - since < s.params.IDLE_s * 1000) return s;
  return { ...stopWatch(s, effects), status: 'stopped' };
}

function withVisible(s: State, ts: number): State {
  return { ...s, attempt: { ...s.attempt, visible_ms: nowVisible(s, ts) } };
}

function countError(s: State, code: number): State {
  const k = String(code);
  return { ...s, attempt: { ...s.attempt, errors: { ...s.attempt.errors, [k]: (s.attempt.errors[k] ?? 0) + 1 } } };
}

const positiveOr = (v: number | null, fallback: number | null) => (v !== null && Number.isFinite(v) && v > 0 ? v : fallback);

// ── the reducer ──

export function reduce(state: State, ev: Event): Result {
  const effects: Effect[] = [];
  const emits: Emit[] = [];
  let s = state;
  const p = s.params;
  switch (ev.type) {
    case 'UNAVAILABLE': {
      s = { ...stopWatch(s, effects), status: 'unavailable' };
      break;
    }
    case 'POLICY': {
      // The server's own numbers (get_gps_guarantee_policy). A missing / bad value keeps what the engine had.
      s = { ...s, params: { ...p, N_s: positiveOr(ev.freshness_s, p.N_s) as number, STALE_MAX_s: positiveOr(ev.stale_max_s, p.STALE_MAX_s) } };
      break;
    }
    case 'DEMAND': {
      if (s.status === 'unavailable') break;
      s = { ...s, demands: s.demands.includes(ev.task_id) ? s.demands : [...s.demands, ev.task_id], last_demand_ts: ev.ts };
      s = startIfWanted(s, ev.ts, effects);
      if (!s.watching && !s.hidden) s = { ...s, status: idleStatus(s) };
      break;
    }
    case 'RELEASE': {
      s = { ...s, demands: s.demands.filter((d) => d !== ev.task_id), last_release_ts: ev.ts };
      break;
    }
    case 'REQUEST': {
      if (s.status === 'unavailable' || s.perm === 'denied') break;
      s = { ...s, requested: true, attempt: s.perm === 'prompt' ? { ...s.attempt, prompt_shown: true } : s.attempt };
      s = startIfWanted(s, ev.ts, effects);
      break;
    }
    case 'PERM': {
      const changed = ev.state !== s.perm;
      s = { ...s, perm: ev.state, perm_ts: ev.ts, attempt: changed ? { ...s.attempt, perm_changes: s.attempt.perm_changes + 1 } : s.attempt };
      if (s.status === 'unavailable') break;
      if (ev.state === 'denied') {
        s = { ...stopWatch(s, effects), status: 'no_permission' };
      } else {
        s = startIfWanted(s, ev.ts, effects);
        if (!s.watching && !s.hidden) s = { ...s, status: idleStatus(s) };
      }
      break;
    }
    case 'ERROR': {
      s = countError(s, ev.code);
      if (s.status === 'unavailable' || !s.watching) break;
      if (ev.code === 1) {
        // D4: code 1 = refused OR OS location off; not separable, so it blocks either way (U8)
        s = { ...stopWatch(s, effects), status: 'no_permission', perm: 'denied' };
      } else if (ev.code === 2) {
        s = bestFix(s, ev.ts) ? classify(s, ev.ts) : { ...s, status: 'location_off' }; // U6: the watch stays
      } else {
        s = classify(s, ev.ts); // U7: 3 = timeout, not fatal, keep watching
      }
      break;
    }
    case 'HIDDEN': {
      if (s.hidden) break;
      const acc = nowVisible(s, ev.ts);
      s = { ...s, hidden: true, visible_ms_accum: acc, armed: s.armed.map((a) => ({ ...a, hid: true })) };
      // SD-O65: the camera app is open → the watch keeps running; any other hide pauses it.
      if (!cameraOpen(s, ev.ts)) {
        s = stopWatch(s, effects);
        if (s.status !== 'unavailable') s = { ...s, status: 'paused' };
      }
      s = withVisible(s, ev.ts);
      break;
    }
    case 'VISIBLE': {
      if (!s.hidden) break;
      // U20: after an ordinary hide nothing heard before it feeds the live status; back from the camera app the fixes
      // heard while it was open are kept (SD-O65). last_good stays either way — its age at the pick decides R2.
      const keep = cameraOpen(s, ev.ts);
      s = {
        ...s, hidden: false, last_visible_ts: ev.ts, fixes: keep ? s.fixes : [], resume_ts: ev.ts,
        attempt: { ...s.attempt, resumed_count: s.attempt.resumed_count + 1 },
      };
      if (s.status === 'unavailable') break;
      s = startIfWanted(s, ev.ts, effects);
      if (!s.watching) s = { ...s, status: idleStatus(s) };
      else if (keep) s = classify(s, ev.ts);
      break;
    }
    case 'FIX': {
      let f = ev.fix;
      const ok = [f.lat, f.lng, f.acc_m, f.fix_ts, f.recv_ts].every((v) => typeof v === 'number' && Number.isFinite(v))
        && f.acc_m > 0 && Math.abs(f.lat) <= 90 && Math.abs(f.lng) <= 180;
      if (!ok || !s.watching) {
        s = { ...s, attempt: { ...s.attempt, rejected_fixes: s.attempt.rejected_fixes + 1 } };
        break;
      }
      let a = s.attempt;
      if (f.fix_ts > f.recv_ts + p.SKEW_ms) {
        f = { ...f, fix_ts: f.recv_ts, skew_clamped: true }; // U18: clamped and flagged
        a = { ...a, skew_clamped: a.skew_clamped + 1 };
      }
      if (f.recv_ts - f.fix_ts > p.N_s * 1000) {
        s = { ...s, attempt: { ...a, stale_ignored: a.stale_ignored + 1 } }; // U14: cached replay
        break;
      }
      a = {
        ...a, fix_count: a.fix_count + 1,
        first_fix_ms: a.first_fix_ms === null && a.watch_started_at !== null ? f.recv_ts - a.watch_started_at : a.first_fix_ms,
        best_accuracy_m: a.best_accuracy_m === null ? f.acc_m : Math.min(a.best_accuracy_m, f.acc_m),
        accuracy_series: [...a.accuracy_series, f.acc_m].slice(-60),
        ttff_after_resume_ms: s.resume_ts !== null ? [...a.ttff_after_resume_ms, f.recv_ts - s.resume_ts] : a.ttff_after_resume_ms,
      };
      const lastGood = f.acc_m <= p.A_hard && (!s.last_good || f.fix_ts >= s.last_good.fix_ts) ? f : s.last_good;
      s = { ...s, fixes: [...s.fixes, f], last_good: lastGood, attempt: a, resume_ts: null };
      if (s.status === 'location_off') s = { ...s, status: 'warming' };
      s = prune(s, f.recv_ts);
      if (!s.hidden) s = classify(s, f.recv_ts);
      break;
    }
    case 'TICK': {
      s = prune(s, ev.ts);
      s = classify(s, ev.ts);
      s = { ...s, armed: s.armed.filter((x) => ev.ts - x.ts <= p.CAMERA_MAX_s * 1000) };
      if (s.hidden && !cameraOpen(s, ev.ts) && s.watching) s = { ...stopWatch(s, effects), status: 'paused' };
      s = idleCheck(s, ev.ts, effects);
      s = withVisible(s, ev.ts);
      break;
    }
    case 'ARM': {
      // B4: remember the accurate fixes that are fresh at the tap (≤ N_s old, ≤ A_hard).
      const atTap = freshFixes(s, ev.ts).filter((f) => f.acc_m <= p.A_hard);
      s = { ...s, armed: [...s.armed.filter((a) => a.photo_id !== ev.photo_id), { photo_id: ev.photo_id, ts: ev.ts, hid: false, fixes: atTap }].slice(-5) };
      break;
    }
    case 'SHUTTER': {
      // The pick. Decided now, once, from fixes taken at or before it (R2); nothing heard later is ever attached.
      s = classify(prune(s, ev.ts), ev.ts);
      const armedEntry = s.armed.find((a) => a.photo_id === ev.photo_id) ?? null;
      s = { ...s, armed: s.armed.filter((a) => a.photo_id !== ev.photo_id) };
      const v = pickVerdict(s, ev.ts);
      // FIX-CCS-POLISH-4 B4 (Adeel ruling 10 Oct): a camera photo whose pick finds no fresh fix may still be verified with
      // the fix held AT THE SHUTTER TAP, when that fix is ≤ A_hard (100 m) and ≤ N_s (15 s) older than the photo's
      // capture moment, and the capture moment lies between the tap and the pick. Gallery / EXIF picks (no tap) never
      // reach this: they keep R2 (fresh at the pick → verified; else ≤ STALE_MAX_s → unverified; else none).
      if (v.kind !== 'verified') {
        const tap = tapFixVerdict(armedEntry, ev, p);
        if (tap) {
          emits.push({ type: 'BUNDLE', photo_id: ev.photo_id, bundle: { ...makeBundle(s, tap.fix, tap.capture_ts, s.status), pick_rule: 'tap_fix' }, verified: true });
          break;
        }
      }
      if (v.kind === 'none') emits.push({ type: 'NO_FIX', photo_id: ev.photo_id, reason: v.reason, status: s.status });
      else emits.push({ type: 'BUNDLE', photo_id: ev.photo_id, bundle: makeBundle(s, v.fix, ev.ts, s.status), verified: v.kind === 'verified' });
      break;
    }
  }
  return { state: s, effects, emits };
}
